import {
  Button,
  Table,
  type TableProps,
} from '@omnipresentgroup/design-system';
import { useClientPolicies } from '@omnipresentgroup/domains';
import type { ClientPolicy } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import { ColumnHeaders, useLeaveSettingsTableConfig } from '../hooks';

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const DEFAULT_PAGE_SIZE = 10;

const EMPTY_ARRAY: ClientPolicy[] = [];
const LOADING_ARRAY = Array.from(
  { length: DEFAULT_PAGE_SIZE },
  () => ({}) as ClientPolicy,
);

export type LeaveSettingsListProps = {
  companyId?: string;
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  optionsOverride?: Omit<TableOptions<ClientPolicy>, 'data' | 'columns'>;
  columnsOverride?: ColumnDef<ClientPolicy, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  onRowClick?: (row: ClientPolicy) => void;
};

export const LeaveSettingsList = ({
  companyId,
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  onRowClick,
}: LeaveSettingsListProps) => {
  const [columnVisibilityState, setColumnVisibilityState] = useState<
    LeaveSettingsListProps['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const { data, isLoading, isError } = useClientPolicies({
    clientId: companyId!,
    enabled: !!companyId,
  });

  const { columns } = useLeaveSettingsTableConfig({
    columnsOverride,
  });

  const tableOptions: Omit<TableOptions<ClientPolicy>, 'data' | 'columns'> = {
    state: {
      columnVisibility: columnVisibilityState,
      columnOrder,
    },
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnOrderChange: setColumnOrder,
    ...optionsOverride,
  };

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : (data ?? EMPTY_ARRAY),
    columns,
    ...tableOptions,
  });

  return (
    <Table
      id={'leave-settings-list'}
      table={table}
      loading={isLoading}
      showHeader={false}
      showErrorState={isError}
      errorStateProps={{
        id: 'leave-settings-list-error',
        title: ERROR_STATE_TITLE,
        description: ERROR_STATE_DESCRIPTION,
        callToAction: <Button onClick={window.location.reload}>Retry</Button>,
      }}
      emptyStateProps={emptyStateProps}
      onRowClick={
        onRowClick
          ? (row: ClientPolicy) => {
              !isLoading && !isError && onRowClick(row);
            }
          : undefined
      }
    />
  );
};
